import {
  Button,
  Flex,
  Text,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatArrow,
  StatHelpText,
  Divider,
  Icon,
  VStack,
  HStack,
  Badge,
  useToast,
  Portal,
} from "@chakra-ui/react";
import { Post } from "api";
import { useConfirmDialog } from "components/confirmModal";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Campaign } from "views/admin/default/reducer";
import { FiTrendingUp, FiDollarSign } from "react-icons/fi";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";
import { useDispatch } from "react-redux";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { IoSparkles } from "react-icons/io5";
import { UpdateCampaignAfterSuggestionApplied } from "views/admin/default/action";

type SuggestedAction = {
  id: number;
  type: "bid" | "daily_cap";
  oldValue: number;
  newValue: number;
  change: number;
};

interface SuggestedCampaignActionsCellProps {
  campaign: Campaign;
}

interface SuggestedActionButtonProps {
  action: SuggestedAction;
  campaignName: string;
  campaignId: string;
}

interface SuggestionComparisonViewProps {
  type: "bid" | "daily_cap";
  oldValue: number;
  newValue: number;
  change: number;
}

const SuggestionComparisonView = ({
  type,
  oldValue,
  newValue,
  change,
}: SuggestionComparisonViewProps) => {
  const difference = newValue - oldValue;
  const isPositive = newValue > oldValue;
  const isBid = type === "bid";

  return (
    <VStack spacing={6} align="stretch" my={4}>
      <Flex gap={4} justifyContent={"center"} alignItems={"flex-start"}>
        <Flex>
          <Stat>
            <StatLabel color="gray.500">
              Current {isBid ? "CPI" : "Budget"}
            </StatLabel>
            <StatNumber fontSize="2xl">${oldValue.toFixed(2)}</StatNumber>
          </Stat>
        </Flex>

        <Flex>
          <Icon as={FiTrendingUp} color="blue.400" boxSize={6} mt={-0.5} />
        </Flex>

        <Flex>
          <Stat>
            <StatLabel color="gray.500">
              Suggested {isBid ? "CPI" : "Budget"}
            </StatLabel>
            <StatNumber fontSize="2xl" color="blue.500">
              ${newValue.toFixed(2)}
            </StatNumber>
            <StatHelpText>
              <StatArrow type={isPositive ? "increase" : "decrease"} />
              {isPositive ? "+" : ""}${difference.toFixed(2)}
            </StatHelpText>
          </Stat>
        </Flex>
      </Flex>

      <Divider />

      <Box bg="blue.50" p={4} borderRadius="md">
        <HStack>
          <Icon as={isBid ? FiTrendingUp : FiDollarSign} color="blue.500" />
          <Text fontWeight="medium">
            {isBid
              ? "Higher bids lead to higher quality."
              : "Increasing your budget allows for more ad delivery throughout the day."}
          </Text>
        </HStack>
        {isBid &&
        <Text mt={2} fontSize="sm" color="gray.600">
          {"Increased CPIs boost your game ranking and provide access to a higher quality user base."}
        </Text>
        }
        <Text mt={2} fontSize="sm" color="gray.600">
          {isBid
            ? "Our algorithm suggests this optimal CPI based on the bids of other similar apps in the same category."
            : "This budget recommendation is based on your campaign's performance potential."}
        </Text>
      </Box>

      <Badge colorScheme="green" alignSelf="start" p={2} borderRadius="md">
        Estimated improvement:{" "}
        {isBid
          ? "Higher quality and more conversions"
          : "Extended reach and improved performance"}
      </Badge>
    </VStack>
  );
};

const SuggestedActionButton = ({
  action,
  campaignName,
  campaignId,
}: SuggestedActionButtonProps) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [tooltipToRight, setTooltipToRight] = useState(true);
  const { ConfirmDialog, openDialog } = useConfirmDialog({
    cancelButtonText: "Cancel",
    confirmButtonText: "Apply",
    centerButtons: true,
    hideCancelButton: true,
  });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;

      let top = rect.top + rect.height / 2;
      let left = rect.right + 10;
      let isToRight = true;

      const tooltipWidth = 400;

      if (left + tooltipWidth > windowWidth) {
        left = rect.left - tooltipWidth - 10;
        isToRight = false;
      }

      setPosition({
        top,
        left,
      });

      setTooltipToRight(isToRight);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    updatePosition();
    timeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 200);
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  }, []);

  useEffect(() => {
    if (isHovered) {
      updatePosition();

      const interval = setInterval(updatePosition, 33);

      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);

      return () => {
        clearInterval(interval);
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isHovered, updatePosition]);

  const handleClick = useCallback(async () => {
    let promise: Promise<boolean> = null;
    switch (action.type) {
      case "bid":
        promise = openDialog({
          title: `Campaign Improvement Available`,
          content: (
            <SuggestionComparisonView
              type={action.type}
              oldValue={action.oldValue}
              newValue={action.newValue}
              change={action.change}
            />
          ),
        });
        break;
      case "daily_cap":
        promise = openDialog({
          title: `Optimize your budgets for ${campaignName}`,
          content: (
            <SuggestionComparisonView
              type={action.type}
              oldValue={action.oldValue}
              newValue={action.newValue}
              change={action.change}
            />
          ),
        });
        break;
    }

    const dialogResult = await promise;
    if (!dialogResult) return;

    try {
      showGlobalLoading("Applying suggested changes...");
      await Post(`/dashboard/campaign-suggestions/${action.id}/apply`, {});
      hideGlobalLoading();

      const updatedValues: { bid?: number; dailyBudgetCap?: number } = {};

      if (action.type === "bid") {
        updatedValues.bid = action.newValue;
      } else if (action.type === "daily_cap") {
        updatedValues.dailyBudgetCap = action.newValue;
      }

      dispatch(UpdateCampaignAfterSuggestionApplied(campaignId, updatedValues));

      toast({
        title: "Success",
        description: "Suggested changes applied successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error applying suggestion:", error);
      hideGlobalLoading();

      toast({
        title: "Error",
        description: "Failed to apply suggested changes",
        status: "error",
        duration: 5000,
      });
    }
  }, [
    action.type,
    action.oldValue,
    action.newValue,
    action.id,
    action.change,
    openDialog,
    campaignName,
    campaignId,
    dispatch,
    toast,
  ]);

  const handleApply = useCallback(async () => {
    try {
      setIsApplying(true);
      showGlobalLoading("Applying suggested changes...");
      await Post(`/dashboard/campaign-suggestions/${action.id}/apply`, {});
      hideGlobalLoading();
      setIsApplying(false);

      setIsHovered(false);

      const updatedValues: { bid?: number; dailyBudgetCap?: number } = {};

      if (action.type === "bid") {
        updatedValues.bid = action.newValue;
      } else if (action.type === "daily_cap") {
        updatedValues.dailyBudgetCap = action.newValue;
      }

      dispatch(UpdateCampaignAfterSuggestionApplied(campaignId, updatedValues));

      toast({
        title: "Success",
        description: "Suggested changes applied successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error applying suggestion:", error);
      hideGlobalLoading();
      setIsApplying(false);

      toast({
        title: "Error",
        description: "Failed to apply suggested changes",
        status: "error",
        duration: 5000,
      });
    }
  }, [
    action.id,
    action.type,
    action.newValue,
    campaignId,
    dispatch,
    toast,
    setIsHovered,
    setIsApplying,
  ]);

  const values = useMemo(() => {
    switch (action.type) {
      case "bid":
        return {
          button: {
            label: "Bid",
          },
        };
      case "daily_cap":
        return {
          button: {
            label: "Budget",
          },
        };

      default:
        return null;
    }
  }, [action.type]);

  if (!values) {
    return <></>;
  }

  return (
    <Box position="relative">
      <Button
        ref={buttonRef}
        size="sm"
        variant={"outline"}
        colorScheme="brandScheme"
        fontSize={"xs"}
        textDecor="underline"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        _hover={{
          bg: "blue.50",
        }}
      >
        <Flex gap={1} alignItems={"flex-start"} justifyContent={"center"}>
          <Icon as={IoSparkles} boxSize={3} />
          <Text>Limited by {values.button.label}</Text>
        </Flex>
      </Button>

      {isHovered && (
        <Portal>
          <Box
            position="fixed"
            top={`${position.top}px`}
            left={`${position.left}px`}
            transform="translateY(-50%)"
            zIndex={99999}
            borderRadius="md"
            boxShadow="2xl"
            border="1px solid"
            borderColor="blue.200"
            bg="white"
            minWidth="350px"
            maxWidth="480px"
            p={0}
            pointerEvents="auto"
            animation={`tooltipFadeIn${
              tooltipToRight ? "Right" : "Left"
            } 0.15s ease-out`}
            sx={{
              "@keyframes tooltipFadeInRight": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(-50%) translateX(-10px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(-50%) translateX(0)",
                },
              },
              "@keyframes tooltipFadeInLeft": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(-50%) translateX(10px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(-50%) translateX(0)",
                },
              },
            }}
            onMouseEnter={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              setIsHovered(true);
            }}
            onMouseLeave={handleMouseLeave}
          >
            <Box
              position="absolute"
              left={tooltipToRight ? "-6px" : "calc(100% - 6px)"}
              top="50%"
              width="12px"
              height="12px"
              transform={
                tooltipToRight
                  ? "translateY(-50%) rotate(45deg)"
                  : "translateY(-50%) rotate(225deg)"
              }
              bg="white"
              boxShadow={
                tooltipToRight
                  ? "-2px 2px 3px rgba(0,0,0,0.1)"
                  : "2px -2px 3px rgba(0,0,0,0.1)"
              }
              borderLeft="1px solid"
              borderBottom="1px solid"
              borderColor="blue.200"
              zIndex={1}
            />

            <Box
              position="relative"
              zIndex={2}
              p={5}
              borderRadius="md"
              bg="white"
              boxShadow="inner"
              border="1px solid"
              borderColor="gray.50"
            >
              {action.type === "bid" ? (
                <Text fontSize={18} textAlign={'center'} fontWeight="bold" mb={4} color="blue.700">
                  Campaign Improvement Available
                </Text>
              ) : (
                <Text fontWeight="bold" mb={2} color="blue.700">
                  Optimize your budgets for {campaignName}
                </Text>
              )}
              <SuggestionComparisonView
                type={action.type}
                oldValue={action.oldValue}
                newValue={action.newValue}
                change={action.change}
              />

              <Flex justifyContent="center" mt={4}>
                <Button
                  colorScheme="brand"
                  size="md"
                  onClick={handleApply}
                  isLoading={isApplying}
                  loadingText="Applying"
                  px={6}
                  fontWeight="semibold"
                >
                  Apply Changes
                </Button>
              </Flex>
            </Box>
          </Box>
        </Portal>
      )}

      {ConfirmDialog}
    </Box>
  );
};

export default function SuggestedCampaignActionsCell({
  campaign,
}: SuggestedCampaignActionsCellProps) {
  const hasSuggestedActions = useMemo(
    () =>
      campaign.suggestions &&
      Array.isArray(campaign.suggestions) &&
      campaign.suggestions.length > 0,
    [campaign]
  );
  const suggestions: SuggestedAction[] = useMemo(() => {
    if (!hasSuggestedActions) {
      return [];
    }

    return campaign.suggestions.map((suggestion) => {
      const type = suggestion.bidIncrease ? "bid" : "daily_cap";
      const change =
        type === "bid"
          ? suggestion.bidIncrease
          : suggestion.budgetCapMultiplier;
      const newValue =
        type === "bid"
          ? campaign.bid + change
          : campaign.dailyBudgetCap * change;
      const oldValue = type === "bid" ? campaign.bid : campaign.dailyBudgetCap;

      return {
        id: suggestion.id,
        type,
        change,
        oldValue,
        newValue,
      };
    });
  }, [hasSuggestedActions, campaign]);

  return (
    <Flex wrap={"wrap"} gap={1}>
      {suggestions.length > 0 ? (
        suggestions.map((suggestion) => (
          <SuggestedActionButton
            key={suggestion.id}
            action={suggestion}
            campaignName={campaign.name}
            campaignId={campaign.id}
          />
        ))
      ) : (
        <></>
      )}
    </Flex>
  );
}
